import { useState, useMemo, useEffect, useRef } from 'react';
import { buildSortedImages, getUniqueImageTypes, isWebGLSupported, isIframe } from "../../components/carousel/carouselHelpers";

export const useCarousel = (images: any[]) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [carouselAssetId, setCarouselAssetId] = useState('');
  const [inModal, setInModal] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUcViewerActive, setIsUcViewerActive] = useState(false);
  const [ucTime, setUcTime] = useState(0);
  const [ucImageSrc, setUcImageSrc] = useState("");
  const [ucAudioSrc, setUcAudioSrc] = useState<string>("");
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

  const isIframeMode = useMemo(() => isIframe(), []);
  const isWebGL = useMemo(() => isWebGLSupported(), []);
  const sortedImages = useMemo(() => buildSortedImages(images), [images]);
  const uniqueImageTypes = useMemo(() => getUniqueImageTypes(sortedImages), [sortedImages]);

  const filteredImages = useMemo(() => {
    if (selectedType === "all") return sortedImages;
    return sortedImages.filter((image) => image.imageType === selectedType);
  }, [selectedType, sortedImages]);

  const selectedImage = useMemo(() => filteredImages[selectedImageIndex], [filteredImages, selectedImageIndex]);

  const handleGoTo = (idx: number) => {
    setSelectedImageIndex(idx);
    setIsPlaying(false);
    setIsUcViewerActive(false);
    setUcImageSrc("");
    requestAnimationFrame(() => {
      thumbnailRefs.current[idx]?.scrollIntoView({ inline: 'center', block: 'nearest' });
    });
  };

  const handleImageClick = (image: any) => {
    const isPano = ["img_bgr_pano", "img_pano"].includes(image?.imageType || '');
    if (isPano && isWebGL) return;
    setInModal(true);
    const isUC = ["img_uc_dewarp", "img_uc"].includes(image?.imageType || '');
    setIsUcViewerActive(isUC);
    if (!isUC) setUcImageSrc("");

    if (isIframeMode) {
      requestAnimationFrame(() => {
        const el = document.getElementById("fullscreen-viewer");
        if (el && el.requestFullscreen) {
          el.requestFullscreen().catch(err => {
            console.warn("Fullscreen failed", err);
          });
        }
      });
    }
  };

  const handleTogglePlay = () => setIsPlaying(prev => !prev);

  const handleCloseViewer = () => {
    setInModal(false);
    setIsUcViewerActive(false);
  };

  useEffect(() => {
    if (!isPlaying || filteredImages.length <= 1) return;
    const interval = setInterval(() => {
      setSelectedImageIndex(prev => (prev + 1) % filteredImages.length);
    }, 500);
    return () => clearInterval(interval);
  }, [isPlaying, filteredImages.length]);

  useEffect(() => {
    const ucUrls = filteredImages
      .filter(img => img.imageType?.includes("uc"))
      .map(img => img.src);

    const left = ucUrls.find(u => u.toLowerCase().includes("left"));
    const nextAudio = left || ucUrls[0] || "";

    // avoid pointless rerenders
    if (nextAudio && nextAudio !== ucAudioSrc) {
      setUcAudioSrc(nextAudio);
    }
  }, [filteredImages, ucAudioSrc, setUcAudioSrc]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!inModal) return;
      if (e.key === 'ArrowRight') {
        const nextIndex = (selectedImageIndex + 1) % filteredImages.length;
        handleGoTo(nextIndex);
      } else if (e.key === 'ArrowLeft') {
        const prevIndex = (selectedImageIndex - 1 + filteredImages.length) % filteredImages.length;
        handleGoTo(prevIndex);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inModal, selectedImageIndex, filteredImages]);

  return {
    // State
    selectedImageIndex,
    selectedImage,
    inModal,
    selectedType,
    isPlaying,
    isUcViewerActive,
    ucTime,
    ucImageSrc,
    ucAudioSrc,
    thumbnailRefs,
    isWebGL,
    uniqueImageTypes,
    filteredImages,
    sortedImages,
    carouselAssetId,
    // Methods
    setSelectedType,
    setCarouselAssetId,
    handleGoTo,
    handleImageClick,
    handleCloseViewer,
    handleTogglePlay,
    setUcTime,
    setUcImageSrc,
    setUcAudioSrc,
    setInModal,
    setIsUcViewerActive
  };
};